import { useState, useEffect, useMemo } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
  type ChartData,
  type ChartOptions,
} from 'chart.js';
import { ProgressTracker } from '@/components/tools/ProgressTracker';
import { GroupedClientSelector } from '@/components/tools/GroupedClientSelector';
import { countOf, getSelectionDisplayText, reasonOf } from '@/components/tools/clientSelectionUtils';
import {
  ReportChoices,
  ReportDownloads,
  ReportFailures,
  ReportStats,
  ReportTable,
  ReportTabs,
  type ReportColumn,
  type ReportStat,
} from '@/components/tools/ReportParts';
import { ToolPage } from '@/components/ui/ToolPage';
import { useToolExecution } from '@/hooks/useToolExecution';
import { CostAnalyticsService } from '@/services/costAnalyticsService';
import { clientService } from '@/services/clientService';
import { ND_CHART_PAL, ND_COLORS } from '@/styles/theme';
import { formatNzDateTime } from '@/utils/relativeTime';
import type { Client } from '@/types';
import type {
  CostAnalyticsParameters,
  CostAnalyticsResult,
  CostRecord,
  CostSummaryRecord,
  ToolResult,
  ToolResultFile,
} from '@/types/tools';
import './CostAnalyticsTool.css';

// The chart needs these parts of chart.js. Registering them twice does no harm.
ChartJS.register(BarController, BarElement, CategoryScale, LinearScale, Legend, Tooltip);

const TIME_PERIOD_OPTIONS = [
  { value: 'last-1-month', label: 'Last month' },
  { value: 'last-3-months', label: 'Last 3 months' },
  { value: 'last-6-months', label: 'Last 6 months' },
  { value: 'current-year', label: 'This year' },
  { value: 'custom', label: 'Dates I choose' },
];

const DEFAULT_PARAMETERS: CostAnalyticsParameters = {
  clientNames: [],
  timePeriod: 'last-3-months',
  granularity: 'MONTHLY',
  includeForecast: true,
  outputFormat: 'csv',
};

/** What AWS charges for one read of its cost data, in US dollars. */
const COST_OF_ONE_READ_USD = 0.01;
/** The chart names this many services and folds the rest into one band. */
const SERVICES_IN_CHART = 6;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-07-01" as "1 Jul 2026". These are calendar dates from AWS, so no time zone applies. */
const formatDay = (day: string): string => {
  const [y, m, d] = day.split('-').map(Number);
  return y && m && d ? `${d} ${MONTHS[m - 1]} ${y}` : day;
};

/** One bucket of the report: "Jul 2026" for a month, "14 Jul" for a day. */
const formatPeriod = (period: string, granularity: 'DAILY' | 'MONTHLY'): string => {
  const [y, m, d] = period.split('-').map(Number);
  if (!y || !m) return period;
  return granularity === 'DAILY' && d ? `${d} ${MONTHS[m - 1]}` : `${MONTHS[m - 1]} ${y}`;
};

/** The day before a calendar date. AWS leaves the end date out, so the last day read is the one before it. */
const dayBefore = (day: string): string => {
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return day;
  const previous = new Date(Date.UTC(y, m - 1, d - 1));
  return previous.toISOString().slice(0, 10);
};

const money = (cost: number | undefined | null): string =>
  cost === undefined || cost === null
    ? ''
    : cost.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const usd = (cost: number): string => `US$${money(cost)}`;

interface ClientTotal {
  clientName: string;
  totalCost: number;
  forecastedCost?: number;
  periods: number;
}

export default function CostAnalyticsTool() {
  const [parameters, setParameters] = useState<CostAnalyticsParameters>(DEFAULT_PARAMETERS);
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [resultFiles, setResultFiles] = useState<ToolResultFile[]>([]);
  const [activeTab, setActiveTab] = useState<string>('clients');

  const { execution, isRunning, execute, cancel, reset } = useToolExecution({
    onCompleted: (result) => {
      if (result.files) {
        setResultFiles(result.files);
      }
    },
    onFailed: (error) => {
      console.error('Cost analytics failed:', error);
    },
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoadingClients(true);
    setClientsError(null);
    try {
      const allClients = await clientService.getAllClients();
      setClients(allClients);
    } catch (error) {
      console.error('Failed to load clients:', error);
      setClientsError(reasonOf(error));
    } finally {
      setLoadingClients(false);
    }
  };

  const handleClientToggle = (clientName: string) => {
    setParameters((prev) => {
      const newClientNames = prev.clientNames.includes(clientName)
        ? prev.clientNames.filter((n) => n !== clientName)
        : [...prev.clientNames, clientName];
      return { ...prev, clientNames: newClientNames };
    });
  };

  const handleSelectClients = (clientNames: string[]) => {
    setParameters((prev) => ({ ...prev, clientNames }));
  };

  const handleExecute = async () => {
    const clientNamesToUse = parameters.clientNames.length === clients.length ? [] : parameters.clientNames;
    const paramsToUse = { ...parameters, clientNames: clientNamesToUse };

    await execute('cost-analytics', paramsToUse as unknown as Record<string, unknown>, async (params, onProgress) => {
      const { result, files } = await CostAnalyticsService.generateReport(
        params as unknown as CostAnalyticsParameters,
        onProgress
      );
      return { type: 'file', files, data: result } as ToolResult;
    });
  };

  /** Back to the form with the same choices. */
  const handleChangeChoices = () => {
    reset();
    setResultFiles([]);
    setActiveTab('clients');
  };

  /** Back to the form with every choice reset. */
  const handleReset = () => {
    reset();
    setResultFiles([]);
    setActiveTab('clients');
    setParameters(DEFAULT_PARAMETERS);
  };

  const isCustom = parameters.timePeriod === 'custom';
  // AWS leaves the end date out and refuses a range that starts and ends on one day.
  const customRangeValid =
    !!parameters.customStartDate && !!parameters.customEndDate && parameters.customStartDate < parameters.customEndDate;

  /** Why the report cannot run yet, or nothing when it can. */
  const missingChoice = (): string | null => {
    if (parameters.clientNames.length === 0) return 'Pick at least one client.';
    if (isCustom && (!parameters.customStartDate || !parameters.customEndDate)) return 'Set the two dates.';
    if (isCustom && !customRangeValid) return 'The end date must be after the start date.';
    return null;
  };
  const blocked = missingChoice();

  // Stacks that share an AWS account are read once, so the count of reads is the count of accounts.
  const accountsToRead = useMemo(() => {
    const chosen = new Set(parameters.clientNames);
    return new Set(clients.filter((c) => chosen.has(c.name)).map((c) => c.config.clientAccountId)).size;
  }, [clients, parameters.clientNames]);
  const readsToMake = accountsToRead * (parameters.includeForecast ? 2 : 1);

  const periodLabel =
    TIME_PERIOD_OPTIONS.find((o) => o.value === parameters.timePeriod)?.label || parameters.timePeriod;

  // Extract result data
  const resultData = execution?.result?.data as CostAnalyticsResult | undefined;
  const finished = execution?.status === 'completed';
  const summaryRows = resultData?.summary || [];
  const serviceBreakdownRows = resultData?.serviceBreakdown || [];
  const failedClientRows = resultData?.failedClients || [];
  const granularity = resultData?.metadata.granularity ?? parameters.granularity;
  const periodWord = granularity === 'DAILY' ? 'day' : 'month';

  // Build aggregated summary by client (across all periods)
  const clientTotals = useMemo(() => {
    const totals = new Map<string, { totalCost: number; forecastedCost?: number; periods: number }>();
    for (const row of summaryRows) {
      const existing = totals.get(row.clientName);
      if (existing) {
        existing.totalCost += row.totalCost;
        existing.periods++;
        if (row.forecastedCost) {
          existing.forecastedCost = (existing.forecastedCost || 0) + row.forecastedCost;
        }
      } else {
        totals.set(row.clientName, {
          totalCost: row.totalCost,
          forecastedCost: row.forecastedCost,
          periods: 1,
        });
      }
    }
    return Array.from(totals.entries())
      .map(([clientName, data]): ClientTotal => ({ clientName, ...data }))
      .sort((a, b) => b.totalCost - a.totalCost);
  }, [summaryRows]);

  // Build service totals across all clients
  const serviceTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const row of serviceBreakdownRows) {
      totals.set(row.service, (totals.get(row.service) || 0) + row.unblendedCost);
    }
    return Array.from(totals.entries())
      .map(([service, cost]) => ({ service, cost: Math.round(cost * 100) / 100 }))
      .sort((a, b) => b.cost - a.cost);
  }, [serviceBreakdownRows]);

  const allServicesTotal = useMemo(() => serviceTotals.reduce((sum, row) => sum + row.cost, 0), [serviceTotals]);
  const forecastTotal = useMemo(
    () => clientTotals.reduce((sum, row) => sum + (row.forecastedCost || 0), 0),
    [clientTotals]
  );

  // The chart: one bar for each month or day, split by the services that cost the most.
  const chart = useMemo(() => {
    const periods = Array.from(new Set(serviceBreakdownRows.map((row) => row.period))).sort();
    if (periods.length === 0) return null;

    const named = serviceTotals.slice(0, SERVICES_IN_CHART).map((row) => row.service);
    const namedSet = new Set(named);
    const hasRest = serviceTotals.length > named.length;
    const REST = 'Every other service';

    const byBand = new Map<string, Map<string, number>>();
    for (const row of serviceBreakdownRows) {
      const band = namedSet.has(row.service) ? row.service : REST;
      const inBand = byBand.get(band) ?? new Map<string, number>();
      inBand.set(row.period, (inBand.get(row.period) || 0) + row.unblendedCost);
      byBand.set(band, inBand);
    }

    const bands = hasRest ? [...named, REST] : named;
    const data: ChartData<'bar'> = {
      labels: periods.map((period) => formatPeriod(period, granularity)),
      datasets: bands.map((band, index) => ({
        label: band,
        data: periods.map((period) => Math.round((byBand.get(band)?.get(period) || 0) * 100) / 100),
        backgroundColor: band === REST ? ND_COLORS.border : ND_CHART_PAL[index % ND_CHART_PAL.length],
        borderWidth: 0,
        borderRadius: 2,
        maxBarThickness: 56,
      })),
    };
    return data;
  }, [serviceBreakdownRows, serviceTotals, granularity]);

  const chartOptions = useMemo((): ChartOptions<'bar'> => {
    // The theme's font, read from the page so the chart follows it.
    const family =
      getComputedStyle(document.documentElement).getPropertyValue('--font-sans').trim() || 'system-ui, sans-serif';
    const tick = { color: ND_COLORS.textFaint, font: { family, size: 12 } };
    const axisTitle = { display: true, color: ND_COLORS.textFaint, font: { family, size: 12, weight: 600 } };
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: ND_COLORS.textDim, font: { family, size: 12 }, boxWidth: 10, boxHeight: 10 },
        },
        tooltip: {
          titleFont: { family },
          bodyFont: { family },
          callbacks: { label: (item) => `${item.dataset.label}: ${usd(item.parsed.y ?? 0)}` },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          ticks: tick,
          title: { ...axisTitle, text: granularity === 'DAILY' ? 'Day' : 'Month' },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          border: { display: false },
          grid: { color: ND_COLORS.border },
          ticks: { ...tick, callback: (value) => `$${Number(value).toLocaleString('en-NZ')}` },
          title: { ...axisTitle, text: 'Cost (USD)' },
        },
      },
    };
  }, [granularity]);

  const showForecast = parameters.includeForecast;

  const forecastColumn: ReportColumn<ClientTotal> = {
    key: 'forecast',
    header: 'Forecast, rest of this month (USD)',
    kind: 'number',
    title: 'What AWS expects the account to spend from today to the end of this month',
    cell: (row) => money(row.forecastedCost),
  };

  const clientColumns: ReportColumn<ClientTotal>[] = [
    { key: 'client', header: 'Client', kind: 'wrap', cell: (row) => row.clientName },
    {
      key: 'cost',
      header: 'Cost (USD)',
      kind: 'number',
      className: 'cost-strong',
      cell: (row) => money(row.totalCost),
    },
    ...(showForecast ? [forecastColumn] : []),
    {
      key: 'periods',
      header: granularity === 'DAILY' ? 'Days with a cost' : 'Months with a cost',
      kind: 'number',
      cell: (row) => row.periods,
    },
  ];

  const periodColumns: ReportColumn<CostSummaryRecord>[] = [
    { key: 'client', header: 'Client', kind: 'wrap', cell: (row) => row.clientName },
    {
      key: 'period',
      header: granularity === 'DAILY' ? 'Day' : 'Month',
      cell: (row) => <span title={row.period}>{formatPeriod(row.period, granularity)}</span>,
    },
    {
      key: 'cost',
      header: 'Cost (USD)',
      kind: 'number',
      className: 'cost-strong',
      cell: (row) => money(row.totalCost),
    },
    {
      key: 'change',
      header: `Change from the ${periodWord} before (USD)`,
      kind: 'number',
      cell: (row) => {
        if (row.costChange === undefined || row.costChange === null) return null;
        const up = row.costChange > 0;
        const down = row.costChange < 0;
        const percent = row.costChangePercent !== undefined ? ` (${up ? '+' : ''}${row.costChangePercent}%)` : '';
        return (
          <span className={up ? 'cost-change cost-change--up' : down ? 'cost-change cost-change--down' : 'cost-change'}>
            {up ? 'Up ' : down ? 'Down ' : ''}
            {money(Math.abs(row.costChange))}
            {percent}
          </span>
        );
      },
    },
    {
      key: 'top',
      header: 'Three largest services (USD)',
      kind: 'wrap',
      cell: (row) => (
        <span className="report-table__sub">
          {row.topServices
            .slice(0, 3)
            .map((t) => `${t.service} ${money(t.cost)}`)
            .join(', ')}
        </span>
      ),
    },
  ];

  const serviceColumns: ReportColumn<{ service: string; cost: number }>[] = [
    { key: 'service', header: 'Service', kind: 'wrap', cell: (row) => row.service },
    { key: 'cost', header: 'Cost (USD)', kind: 'number', className: 'cost-strong', cell: (row) => money(row.cost) },
    {
      key: 'share',
      header: 'Share of the total',
      kind: 'number',
      cell: (row) => `${allServicesTotal > 0 ? Math.round((row.cost / allServicesTotal) * 10000) / 100 : 0}%`,
    },
  ];

  const lineColumns: ReportColumn<CostRecord>[] = [
    { key: 'client', header: 'Client', kind: 'wrap', cell: (row) => row.clientName },
    {
      key: 'period',
      header: granularity === 'DAILY' ? 'Day' : 'Month',
      cell: (row) => <span title={row.period}>{formatPeriod(row.period, granularity)}</span>,
    },
    { key: 'service', header: 'Service', kind: 'wrap', cell: (row) => row.service },
    { key: 'cost', header: 'Cost (USD)', kind: 'number', cell: (row) => money(row.unblendedCost) },
  ];

  const stats: ReportStat[] = resultData
    ? [
        { label: 'Total cost', value: usd(resultData.metadata.totalCost) },
        { label: 'Clients read', value: resultData.metadata.clientsProcessed.toLocaleString() },
        ...(showForecast && forecastTotal > 0
          ? [{ label: 'Forecast for the rest of this month', value: usd(forecastTotal) }]
          : []),
        ...(resultData.metadata.clientsFailed > 0
          ? [{ label: 'Clients not read', value: resultData.metadata.clientsFailed.toLocaleString() }]
          : []),
      ]
    : [];

  return (
    <ToolPage
      title="Cost analytics"
      subtitle="See a client's AWS cost by service and by month, and where it is heading."
    >
      {clientsError && (
        <Alert variant="danger">
          The client list did not load, so there is nothing to pick from. {clientsError}{' '}
          <Button variant="link" className="report-inline-link" onClick={loadClients}>
            Try again
          </Button>
        </Alert>
      )}

      {!execution && (
        <Card className="report-form">
          <Card.Body>
            <div className="report-form__grid">
              <div className="report-form__col">
                <h2 className="section-label">Clients</h2>
                <GroupedClientSelector
                  clients={clients}
                  selectedClientNames={parameters.clientNames}
                  onClientToggle={handleClientToggle}
                  onSelectClients={handleSelectClients}
                  disabled={isRunning}
                  loading={loadingClients}
                />
              </div>

              <div className="report-form__col">
                <h2 className="section-label">Period</h2>
                <Form.Group className="report-field" controlId="cost-period">
                  <Form.Label>Period</Form.Label>
                  <Form.Select
                    value={parameters.timePeriod}
                    onChange={(e) =>
                      setParameters((prev) => ({
                        ...prev,
                        timePeriod: e.target.value,
                        ...(e.target.value !== 'custom'
                          ? { customStartDate: undefined, customEndDate: undefined }
                          : {}),
                      }))
                    }
                    disabled={isRunning}
                  >
                    {TIME_PERIOD_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                  {!isCustom && <Form.Text>Whole calendar months. The month in progress is left out.</Form.Text>}
                </Form.Group>

                {isCustom && (
                  <Form.Group className="report-field">
                    <Form.Label>Dates</Form.Label>
                    <div className="report-range">
                      <Form.Control
                        type="date"
                        aria-label="Start date"
                        value={parameters.customStartDate ?? ''}
                        onChange={(e) =>
                          setParameters((prev) => ({ ...prev, customStartDate: e.target.value || undefined }))
                        }
                        disabled={isRunning}
                      />
                      <span className="report-range__to">to</span>
                      <Form.Control
                        type="date"
                        aria-label="End date"
                        value={parameters.customEndDate ?? ''}
                        min={parameters.customStartDate}
                        onChange={(e) =>
                          setParameters((prev) => ({ ...prev, customEndDate: e.target.value || undefined }))
                        }
                        disabled={isRunning}
                      />
                    </div>
                    {parameters.customStartDate && parameters.customEndDate && !customRangeValid ? (
                      <span className="report-field__error">The end date must be after the start date.</span>
                    ) : (
                      <Form.Text>The end date itself is not counted: the last day read is the day before it.</Form.Text>
                    )}
                  </Form.Group>
                )}

                <Form.Group className="report-field">
                  <Form.Label>Split the cost by</Form.Label>
                  <div className="report-checks">
                    <Form.Check
                      type="radio"
                      id="cost-granularity-month"
                      label="Month"
                      name="granularity"
                      checked={parameters.granularity === 'MONTHLY'}
                      onChange={() => setParameters((prev) => ({ ...prev, granularity: 'MONTHLY' }))}
                      disabled={isRunning}
                    />
                    <Form.Check
                      type="radio"
                      id="cost-granularity-day"
                      label="Day"
                      name="granularity"
                      checked={parameters.granularity === 'DAILY'}
                      onChange={() => setParameters((prev) => ({ ...prev, granularity: 'DAILY' }))}
                      disabled={isRunning}
                    />
                  </div>
                </Form.Group>

                <Form.Group className="report-field">
                  <Form.Check
                    type="switch"
                    id="cost-forecast"
                    label="Add this month's forecast"
                    checked={parameters.includeForecast}
                    onChange={(e) => setParameters((prev) => ({ ...prev, includeForecast: e.target.checked }))}
                    disabled={isRunning}
                  />
                  <Form.Text>
                    What AWS expects each account to spend from today to the end of this month. There is none on the
                    first day of a month.
                  </Form.Text>
                </Form.Group>
              </div>

              <div className="report-form__col">
                <h2 className="section-label">Files</h2>
                <Form.Group className="report-field" controlId="cost-format">
                  <Form.Label>File format</Form.Label>
                  <Form.Select
                    value={parameters.outputFormat}
                    onChange={(e) =>
                      setParameters((prev) => ({ ...prev, outputFormat: e.target.value as 'csv' | 'json' }))
                    }
                    disabled={isRunning}
                  >
                    <option value="csv">CSV</option>
                    <option value="json">CSV and JSON</option>
                  </Form.Select>
                </Form.Group>
                <p className="report-note">
                  AWS cost data is about a day behind. Every cost on this page is in US dollars.
                </p>
              </div>
            </div>
          </Card.Body>
          <div className="report-form__foot">
            <p className="report-form__hint">
              {blocked ||
                `Reads ${countOf(accountsToRead, 'AWS account')} for ${countOf(parameters.clientNames.length, 'client')}, one after the other. AWS bills US$${COST_OF_ONE_READ_USD.toFixed(2)} for each read of cost data: up to ${countOf(readsToMake, 'read')}, ${usd(readsToMake * COST_OF_ONE_READ_USD)}.`}
            </p>
            <Button variant="primary" onClick={handleExecute} disabled={blocked !== null || isRunning}>
              Run report
            </Button>
          </div>
        </Card>
      )}

      {execution && (
        <Card className="report-run">
          <Card.Body>
            <ReportChoices
              running={isRunning}
              onChange={handleChangeChoices}
              onStartOver={handleReset}
              choices={[
                { label: 'Clients', value: getSelectionDisplayText(clients, parameters.clientNames) },
                {
                  label: 'Period',
                  value:
                    isCustom && parameters.customStartDate && parameters.customEndDate
                      ? `${formatDay(parameters.customStartDate)} to ${formatDay(dayBefore(parameters.customEndDate))}`
                      : periodLabel,
                },
                { label: 'Split by', value: parameters.granularity === 'DAILY' ? 'Day' : 'Month' },
                { label: 'Forecast', value: parameters.includeForecast ? 'Included' : 'Left out' },
                { label: 'File format', value: parameters.outputFormat === 'json' ? 'CSV and JSON' : 'CSV' },
              ]}
            />
            <div className="report-run__progress">
              <ProgressTracker
                status={execution.status}
                progress={execution.progress}
                error={execution.error}
                startedAt={execution.startedAt}
                completedAt={execution.completedAt}
                onStop={cancel}
                unit="account"
              />
            </div>
          </Card.Body>
        </Card>
      )}

      {finished && resultData && (
        <>
          <p className="report-result__note">
            {usd(resultData.metadata.totalCost)} across {countOf(clientTotals.length, 'client')}, from{' '}
            {formatDay(resultData.metadata.startDate)} to {formatDay(dayBefore(resultData.metadata.endDate))}. Read{' '}
            {formatNzDateTime(resultData.metadata.exportDate)} New Zealand time.
          </p>

          <ReportStats stats={stats} />

          <ReportFailures failures={failedClientRows} />

          {summaryRows.length === 0 && (
            <Alert variant="warning">
              AWS returned no cost for these clients in this period. Change the choices to try a longer period, or check
              the clients above that could not be read.
            </Alert>
          )}

          {chart && (
            <Card className="report-card">
              <Card.Header className="report-card__head">
                <h2 className="report-card__title">
                  Cost by {periodWord}
                  <span className="report-card__sub">
                    US dollars, all chosen clients together, split by the services that cost the most
                  </span>
                </h2>
              </Card.Header>
              <div className="cost-chart">
                <Bar data={chart} options={chartOptions} />
              </div>
            </Card>
          )}

          <Card className="report-card">
            <Card.Header className="report-card__head">
              <h2 className="report-card__title">Tables</h2>
              <ReportDownloads files={resultFiles} />
            </Card.Header>
            <ReportTabs
              activeKey={activeTab}
              onSelect={setActiveTab}
              tabs={[
                { key: 'clients', label: 'By client', count: clientTotals.length },
                { key: 'periods', label: `By client and ${periodWord}`, count: summaryRows.length },
                { key: 'services', label: 'By service', count: serviceTotals.length },
                { key: 'lines', label: 'Every line', count: serviceBreakdownRows.length },
              ]}
            />
            {activeTab === 'clients' && (
              <>
                <ReportTable key="clients" columns={clientColumns} rows={clientTotals} empty="No cost was found." />
                {clientTotals.length > 1 && (
                  <div className="cost-total">
                    <span>Total for {countOf(clientTotals.length, 'client')}</span>
                    <span className="numeric">{usd(clientTotals.reduce((sum, row) => sum + row.totalCost, 0))}</span>
                  </div>
                )}
              </>
            )}
            {activeTab === 'periods' && (
              <ReportTable key="periods" columns={periodColumns} rows={summaryRows} empty="No cost was found." />
            )}
            {activeTab === 'services' && (
              <ReportTable key="services" columns={serviceColumns} rows={serviceTotals} empty="No cost was found." />
            )}
            {activeTab === 'lines' && (
              <ReportTable
                key="lines"
                columns={lineColumns}
                rows={serviceBreakdownRows}
                empty="No cost was found for any service."
              />
            )}
          </Card>
        </>
      )}
    </ToolPage>
  );
}
